import type { FormEditorEntity } from "./form-editor-entity.types";

const TT = "admin.shared" as const;

/**
 * Full i18next key paths for the admin form editor (`public/locales/en/translation.json` under `admin.shared`).
 * Ticket-type and job-type editors share this tree (`formEditor` / `formRules`).
 */
export const ticketAdminFormPaths = {
	formEditor: {
		saveSuccess: `${TT}.formEditor.saveSuccess`,
		saveError: `${TT}.formEditor.saveError`,
		draftHydrateWarnings: `${TT}.formEditor.draftHydrateWarnings`,
		breadcrumb: {
			list: `${TT}.formEditor.breadcrumb.list`,
			editor: `${TT}.formEditor.breadcrumb.editor`,
		},
		pageTitle: `${TT}.formEditor.pageTitle`,
		cancel: `${TT}.formEditor.cancel`,
		save: `${TT}.formEditor.save`,
		unsaved: {
			title: `${TT}.formEditor.unsaved.title`,
			body: `${TT}.formEditor.unsaved.body`,
			stay: `${TT}.formEditor.unsaved.stay`,
			discard: `${TT}.formEditor.unsaved.discard`,
			saveAndContinue: `${TT}.formEditor.unsaved.saveAndContinue`,
		},
		tabBuilder: `${TT}.formEditor.tabBuilder`,
		tabRules: `${TT}.formEditor.tabRules`,
		hidePreview: `${TT}.formEditor.hidePreview`,
		showPreview: `${TT}.formEditor.showPreview`,
		a11y: {
			breadcrumb: `${TT}.formEditor.a11y.breadcrumb`,
			formCanvasDropRegion: `${TT}.formEditor.a11y.formCanvasDropRegion`,
			formStructureList: `${TT}.formEditor.a11y.formStructureList`,
			dragHandleSection: `${TT}.formEditor.a11y.dragHandleSection`,
			collapseSection: `${TT}.formEditor.a11y.collapseSection`,
			expandSection: `${TT}.formEditor.a11y.expandSection`,
			editSectionProperties: `${TT}.formEditor.a11y.editSectionProperties`,
			sectionFieldsList: `${TT}.formEditor.a11y.sectionFieldsList`,
			sectionAppendDrop: `${TT}.formEditor.a11y.sectionAppendDrop`,
			fieldRowSummary: `${TT}.formEditor.a11y.fieldRowSummary`,
			dragHandleField: `${TT}.formEditor.a11y.dragHandleField`,
			editFieldProperties: `${TT}.formEditor.a11y.editFieldProperties`,
			paletteRow: `${TT}.formEditor.a11y.paletteRow`,
		},
		builder: {
			title: `${TT}.formEditor.builder.title`,
			subtitle: `${TT}.formEditor.builder.subtitle`,
			emptyTitle: `${TT}.formEditor.builder.emptyTitle`,
			emptySubtitle: `${TT}.formEditor.builder.emptySubtitle`,
			addSection: `${TT}.formEditor.builder.addSection`,
			sectionHeader: `${TT}.formEditor.builder.sectionHeader`,
			moveUp: `${TT}.formEditor.builder.moveUp`,
			moveDown: `${TT}.formEditor.builder.moveDown`,
			deleteFieldNamed: `${TT}.formEditor.builder.deleteFieldNamed`,
			clearSectionFields: `${TT}.formEditor.builder.clearSectionFields`,
			deleteEmptySection: `${TT}.formEditor.builder.deleteEmptySection`,
			sectionFieldCount: `${TT}.formEditor.builder.sectionFieldCount`,
			dropFieldInSection: `${TT}.formEditor.builder.dropFieldInSection`,
			dropFieldInSectionDragOver: `${TT}.formEditor.builder.dropFieldInSectionDragOver`,
			sectionTagCollapsibleExpandedByDefault: `${TT}.formEditor.builder.sectionTagCollapsibleExpandedByDefault`,
			sectionTagCollapsibleCollapsedByDefault: `${TT}.formEditor.builder.sectionTagCollapsibleCollapsedByDefault`,
		},
		defaults: {
			newSectionLabel: `${TT}.formEditor.defaults.newSectionLabel`,
			urlPlaceholder: `${TT}.formEditor.defaults.urlPlaceholder`,
		},
		validation: {
			invalidEmail: `${TT}.formEditor.validation.invalidEmail`,
			invalidUrl: `${TT}.formEditor.validation.invalidUrl`,
		},
		palette: {
			title: `${TT}.formEditor.palette.title`,
			subtitle: `${TT}.formEditor.palette.subtitle`,
			types: {
				text: `${TT}.formEditor.palette.types.text`,
				textArea: `${TT}.formEditor.palette.types.textArea`,
				number: `${TT}.formEditor.palette.types.number`,
				email: `${TT}.formEditor.palette.types.email`,
				phone: `${TT}.formEditor.palette.types.phone`,
				date: `${TT}.formEditor.palette.types.date`,
				time: `${TT}.formEditor.palette.types.time`,
				dateTime: `${TT}.formEditor.palette.types.dateTime`,
				select: `${TT}.formEditor.palette.types.select`,
				radio: `${TT}.formEditor.palette.types.radio`,
				checkbox: `${TT}.formEditor.palette.types.checkbox`,
				file: `${TT}.formEditor.palette.types.file`,
				url: `${TT}.formEditor.palette.types.url`,
				multiSelect: `${TT}.formEditor.palette.types.multiSelect`,
				users: `${TT}.formEditor.palette.types.users`,
				signature: `${TT}.formEditor.palette.types.signature`,
				address: `${TT}.formEditor.palette.types.address`,
			},
		},
		preview: {
			title: `${TT}.formEditor.preview.title`,
			description: `${TT}.formEditor.preview.description`,
			close: `${TT}.formEditor.preview.close`,
			selectPlaceholder: `${TT}.formEditor.preview.selectPlaceholder`,
		},
		properties: {
			sectionTitle: `${TT}.formEditor.properties.sectionTitle`,
			subtitle: `${TT}.formEditor.properties.subtitle`,
			close: `${TT}.formEditor.properties.close`,
			sectionLabel: `${TT}.formEditor.properties.sectionLabel`,
			sectionLabelPlaceholder: `${TT}.formEditor.properties.sectionLabelPlaceholder`,
			description: `${TT}.formEditor.properties.description`,
			descriptionPlaceholder: `${TT}.formEditor.properties.descriptionPlaceholder`,
			sectionMaxFields: `${TT}.formEditor.properties.sectionMaxFields`,
			sectionMaxFieldsHint: `${TT}.formEditor.properties.sectionMaxFieldsHint`,
			sectionMaxFieldsPlaceholder: `${TT}.formEditor.properties.sectionMaxFieldsPlaceholder`,
			collapsible: `${TT}.formEditor.properties.collapsible`,
			defaultExpanded: `${TT}.formEditor.properties.defaultExpanded`,
			sectionTip: `${TT}.formEditor.properties.sectionTip`,
			deleteSection: `${TT}.formEditor.properties.deleteSection`,
			fieldTitle: `${TT}.formEditor.properties.fieldTitle`,
			fieldType: `${TT}.formEditor.properties.fieldType`,
			fieldLabel: `${TT}.formEditor.properties.fieldLabel`,
			fieldLabelPlaceholder: `${TT}.formEditor.properties.fieldLabelPlaceholder`,
			numberApiDataType: `${TT}.formEditor.properties.numberApiDataType`,
			numberApiDataTypeHint: `${TT}.formEditor.properties.numberApiDataTypeHint`,
			numberApiDataTypeInteger: `${TT}.formEditor.properties.numberApiDataTypeInteger`,
			numberApiDataTypeDecimal: `${TT}.formEditor.properties.numberApiDataTypeDecimal`,
			textAreaRows: `${TT}.formEditor.properties.textAreaRows`,
			textAreaRowsHint: `${TT}.formEditor.properties.textAreaRowsHint`,
			dropZoneHint: `${TT}.formEditor.properties.dropZoneHint`,
			placeholder: `${TT}.formEditor.properties.placeholder`,
			dropZoneHintPlaceholder: `${TT}.formEditor.properties.dropZoneHintPlaceholder`,
			placeholderHint: `${TT}.formEditor.properties.placeholderHint`,
			helpText: `${TT}.formEditor.properties.helpText`,
			required: `${TT}.formEditor.properties.required`,
			filePresentation: `${TT}.formEditor.properties.filePresentation`,
			filePresentationDropZone: `${TT}.formEditor.properties.filePresentationDropZone`,
			filePresentationButton: `${TT}.formEditor.properties.filePresentationButton`,
			fileAccept: `${TT}.formEditor.properties.fileAccept`,
			fileAcceptPlaceholder: `${TT}.formEditor.properties.fileAcceptPlaceholder`,
			fileAcceptHint: `${TT}.formEditor.properties.fileAcceptHint`,
			allowMultipleFiles: `${TT}.formEditor.properties.allowMultipleFiles`,
			maxFiles: `${TT}.formEditor.properties.maxFiles`,
			maxFilesPlaceholder: `${TT}.formEditor.properties.maxFilesPlaceholder`,
			maxFilesHint: `${TT}.formEditor.properties.maxFilesHint`,
			options: `${TT}.formEditor.properties.options`,
			optionLabelField: `${TT}.formEditor.properties.optionLabelField`,
			removeOptionNamed: `${TT}.formEditor.properties.removeOptionNamed`,
			removeOption: `${TT}.formEditor.properties.removeOption`,
			newOptionLabel: `${TT}.formEditor.properties.newOptionLabel`,
			addOption: `${TT}.formEditor.properties.addOption`,
			alphabetizeOptions: `${TT}.formEditor.properties.alphabetizeOptions`,
			orderAz: `${TT}.formEditor.properties.orderAz`,
			dragOption: `${TT}.formEditor.properties.dragOption`,
			signaturePlaceholder: `${TT}.formEditor.properties.signaturePlaceholder`,
			deleteField: `${TT}.formEditor.properties.deleteField`,
		},
	},
	formRules: {
		title: `${TT}.formEditor.formRules.title`,
		subtitle: `${TT}.formEditor.formRules.subtitle`,
		emptyTitle: `${TT}.formEditor.formRules.emptyTitle`,
		emptySubtitle: `${TT}.formEditor.formRules.emptySubtitle`,
		addRule: `${TT}.formEditor.formRules.addRule`,
		editRule: `${TT}.formEditor.formRules.editRule`,
		deleteRule: `${TT}.formEditor.formRules.deleteRule`,
		duplicateRule: `${TT}.formEditor.formRules.duplicateRule`,
		toggleEnable: `${TT}.formEditor.formRules.toggleEnable`,
		statusEnabled: `${TT}.formEditor.formRules.statusEnabled`,
		statusDisabled: `${TT}.formEditor.formRules.statusDisabled`,
		conditionCount: `${TT}.formEditor.formRules.conditionCount`,
		conditionCount_one: `${TT}.formEditor.formRules.conditionCount_one`,
		conditionCount_other: `${TT}.formEditor.formRules.conditionCount_other`,
		actionCount: `${TT}.formEditor.formRules.actionCount`,
		actionCount_one: `${TT}.formEditor.formRules.actionCount_one`,
		actionCount_other: `${TT}.formEditor.formRules.actionCount_other`,
		conditionLogic: {
			matchLabel: `${TT}.formEditor.formRules.conditionLogic.matchLabel`,
			allToggle: `${TT}.formEditor.formRules.conditionLogic.allToggle`,
			anyToggle: `${TT}.formEditor.formRules.conditionLogic.anyToggle`,
			allMatchHint: `${TT}.formEditor.formRules.conditionLogic.allMatchHint`,
			anyMatchHint: `${TT}.formEditor.formRules.conditionLogic.anyMatchHint`,
			all: `${TT}.formEditor.formRules.conditionLogic.all`,
			any: `${TT}.formEditor.formRules.conditionLogic.any`,
			allHint: `${TT}.formEditor.formRules.conditionLogic.allHint`,
			anyHint: `${TT}.formEditor.formRules.conditionLogic.anyHint`,
		},
		operators: {
			equals: `${TT}.formEditor.formRules.operators.equals`,
			not_equals: `${TT}.formEditor.formRules.operators.not_equals`,
			contains: `${TT}.formEditor.formRules.operators.contains`,
			not_contains: `${TT}.formEditor.formRules.operators.not_contains`,
			greater_than: `${TT}.formEditor.formRules.operators.greater_than`,
			less_than: `${TT}.formEditor.formRules.operators.less_than`,
			greater_than_or_equal: `${TT}.formEditor.formRules.operators.greater_than_or_equal`,
			less_than_or_equal: `${TT}.formEditor.formRules.operators.less_than_or_equal`,
			is_empty: `${TT}.formEditor.formRules.operators.is_empty`,
			is_not_empty: `${TT}.formEditor.formRules.operators.is_not_empty`,
			is_checked: `${TT}.formEditor.formRules.operators.is_checked`,
			is_not_checked: `${TT}.formEditor.formRules.operators.is_not_checked`,
			in: `${TT}.formEditor.formRules.operators.in`,
			not_in: `${TT}.formEditor.formRules.operators.not_in`,
		},
		actions: {
			show_field: `${TT}.formEditor.formRules.actions.show_field`,
			hide_field: `${TT}.formEditor.formRules.actions.hide_field`,
			enable_field: `${TT}.formEditor.formRules.actions.enable_field`,
			disable_field: `${TT}.formEditor.formRules.actions.disable_field`,
			set_required: `${TT}.formEditor.formRules.actions.set_required`,
			set_optional: `${TT}.formEditor.formRules.actions.set_optional`,
			set_validation: `${TT}.formEditor.formRules.actions.set_validation`,
		},
		filters: {
			label: `${TT}.formEditor.formRules.filters.label`,
			all: `${TT}.formEditor.formRules.filters.all`,
			enabledOnly: `${TT}.formEditor.formRules.filters.enabledOnly`,
			disabledOnly: `${TT}.formEditor.formRules.filters.disabledOnly`,
			empty: `${TT}.formEditor.formRules.filters.empty`,
		},
		sort: {
			label: `${TT}.formEditor.formRules.sort.label`,
			nameAsc: `${TT}.formEditor.formRules.sort.nameAsc`,
			nameDesc: `${TT}.formEditor.formRules.sort.nameDesc`,
			newest: `${TT}.formEditor.formRules.sort.newest`,
			oldest: `${TT}.formEditor.formRules.sort.oldest`,
		},
		modal: {
			createTitle: `${TT}.formEditor.formRules.modal.createTitle`,
			editTitle: `${TT}.formEditor.formRules.modal.editTitle`,
			subtitle: `${TT}.formEditor.formRules.modal.subtitle`,
			name: `${TT}.formEditor.formRules.modal.name`,
			namePlaceholder: `${TT}.formEditor.formRules.modal.namePlaceholder`,
			description: `${TT}.formEditor.formRules.modal.description`,
			descriptionPlaceholder: `${TT}.formEditor.formRules.modal.descriptionPlaceholder`,
			enabled: `${TT}.formEditor.formRules.modal.enabled`,
			optional: `${TT}.formEditor.formRules.modal.optional`,
			conditionsSection: `${TT}.formEditor.formRules.modal.conditionsSection`,
			conditionsHint: `${TT}.formEditor.formRules.modal.conditionsHint`,
			conditionsEmpty: `${TT}.formEditor.formRules.modal.conditionsEmpty`,
			actionsSection: `${TT}.formEditor.formRules.modal.actionsSection`,
			actionsHint: `${TT}.formEditor.formRules.modal.actionsHint`,
			conditionFieldLabel: `${TT}.formEditor.formRules.modal.conditionFieldLabel`,
			conditionOperatorLabel: `${TT}.formEditor.formRules.modal.conditionOperatorLabel`,
			conditionValueLabel: `${TT}.formEditor.formRules.modal.conditionValueLabel`,
			actionTypeLabel: `${TT}.formEditor.formRules.modal.actionTypeLabel`,
			targetFieldLabel: `${TT}.formEditor.formRules.modal.targetFieldLabel`,
			addCondition: `${TT}.formEditor.formRules.modal.addCondition`,
			addAction: `${TT}.formEditor.formRules.modal.addAction`,
			removeCondition: `${TT}.formEditor.formRules.modal.removeCondition`,
			removeAction: `${TT}.formEditor.formRules.modal.removeAction`,
			selectField: `${TT}.formEditor.formRules.modal.selectField`,
			selectOperator: `${TT}.formEditor.formRules.modal.selectOperator`,
			selectAction: `${TT}.formEditor.formRules.modal.selectAction`,
			valuePlaceholder: `${TT}.formEditor.formRules.modal.valuePlaceholder`,
			save: `${TT}.formEditor.formRules.modal.save`,
			cancel: `${TT}.formEditor.formRules.modal.cancel`,
			close: `${TT}.formEditor.formRules.modal.close`,
		},
		delete: {
			title: `${TT}.formEditor.formRules.delete.title`,
			body: `${TT}.formEditor.formRules.delete.body`,
			confirm: `${TT}.formEditor.formRules.delete.confirm`,
			cancel: `${TT}.formEditor.formRules.delete.cancel`,
		},
		validation: {
			nameRequired: `${TT}.formEditor.formRules.validation.nameRequired`,
			actionsRequired: `${TT}.formEditor.formRules.validation.actionsRequired`,
			conditionFieldRequired: `${TT}.formEditor.formRules.validation.conditionFieldRequired`,
			actionTargetRequired: `${TT}.formEditor.formRules.validation.actionTargetRequired`,
			valueRequired: `${TT}.formEditor.formRules.validation.valueRequired`,
		},
		toast: {
			created: `${TT}.formEditor.formRules.toast.created`,
			updated: `${TT}.formEditor.formRules.toast.updated`,
			updateFailed: `${TT}.formEditor.formRules.toast.updateFailed`,
			deleted: `${TT}.formEditor.formRules.toast.deleted`,
			deleteFailed: `${TT}.formEditor.formRules.toast.deleteFailed`,
		},
	},
} as const;

/** Same keys as {@link ticketAdminFormPaths}; all form-editor entities use `admin.shared`. */
export const jobAdminFormPaths = ticketAdminFormPaths;
/** Same keys as {@link ticketAdminFormPaths}; record-type form editor shares the namespace. */
export const recordAdminFormPaths = ticketAdminFormPaths;
/** Same keys as {@link ticketAdminFormPaths}; asset-type form editor shares the namespace. */
export const assetAdminFormPaths = ticketAdminFormPaths;

export type AdminFormI18nPaths = typeof ticketAdminFormPaths;

export function pickAdminFormPaths(
	_entity: FormEditorEntity,
): AdminFormI18nPaths {
	switch (_entity as FormEditorEntity) {
		case "job":
			return jobAdminFormPaths;
		case "record":
			return recordAdminFormPaths;
		case "asset":
			return assetAdminFormPaths;
		default:
			return ticketAdminFormPaths;
	}
}
